import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/Avatar";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/Select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/reusableComponents/Dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/reusableComponents/alert-dialog";
import { Label } from "../ui/Label";
import { 
  UserPlusIcon, 
  Users, 
  Search,
  Filter,
  Grid3X3,
  List,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Shield,
  UserCheck,
  UserX,
  Settings
} from "lucide-react";

// Données factices des utilisateurs
const mockUsers= [
  {
    id: 1,
    name: "Jihade El malikuli",
    username: "jihade.elmalikuli",
    role: "HYPERADMIN",
    email: "jihade@jm-interim.com",
    phone: "+33551037325",
    active: true,
    permissions: [{ page: "Tous", access: "full" }],
  },
  {
    id: 2,
    name: "Angélique ROSE",
    username: "angelique.rose",
    role: "SUPERADMIN",
    email: "pale@jm-interim.com",
    phone: "0650634546",
    active: true,
    permissions: [
     { page: "Planning", access: "full" }, { page: "Document", access: "full" }, { page: "Candidat", access: "full" },
      { page: "Commande", access: "full" }, { page: "Relevés d'heures", access: "full" }, { page: "Facture", access: "full" },
      { page: "Devis", access: "full" }, { page: "Contrat", access: "full" }
    ],
  },
  {
    id: 3,
    name: "Delmon Franck",
    username: "franck.delmon",
    role: "SUPERADMIN",
    email: "brive@jm-interim.com",
    phone: "0650251260",
    active: true,
    permissions: [
      { page: "Planning", access: "full" }, { page: "Document", access: "full" }, { page: "Candidat", access: "full" },
      { page: "Commande", access: "full" }, { page: "Relevés d'heures", access: "full" }, { page: "Facture", access: "full" },
      { page: "Devis", access: "full" }, { page: "Contrat", access: "full" }
    ],
  },
  {
    id: 4,
    name: "Antoine Lacroix",
    username: "antoine.lacroix",
    role: "ADMIN",
    email: "brive@jm-interim.com",
    phone: "0555174979",
    active: true,
    permissions: [
        { page: "Planning", access: "full" },
        { page: "Prolongation", access: "read" },
        { page: "Candidat", access: "read" },
        { page: "Commande", access: "full" },
        { page: "Relevés d'heures", access: "full" },
    ],
  },
  {
    id: 5,
    name: "Marie Dubois",
    username: "marie.dubois",
    role: "USER",
    email: "paris@jm-interim.com",
    phone: "0155237845",
    active: false,
     permissions: [
        { page: "Planning", access: "read" },
        { page: "Relevés d'heures", access: "read" },
      ],
  },
  {
    id: 6,
    name: "Thomas Martin",
    username: "thomas.martin",
    role: "USER",
    email: "lyon@jm-interim.com",
    phone: "0478912365",
    active: false,
     permissions: [
        { page: "Planning", access: "read" },
        { page: "Relevés d'heures", access: "read" },
      ],
  }
];

// Rôles et permissions avec niveaux d'accès
const roles = ["HYPERADMIN", "SUPERADMIN", "ADMIN", "USER"];

const rolePermissions = {
  HYPERADMIN: [{ page: "Tous", access: "full" }],
  SUPERADMIN: [
    { page: "Planning", access: "full" },
    { page: "Annonces", access: "full" },
    { page: "Candidat", access: "full" },
    { page: "Commande", access: "full" },
    { page: "Relevés d'heures", access: "full" },
    { page: "Facture", access: "full" },
  ],
  ADMIN: [
    { page: "Planning", access: "full" },
    { page: "Prolongation", access: "read" },
    { page: "Candidat", access: "read" },
    { page: "Commande", access: "full" },
    { page: "Relevés d'heures", access: "full" },
  ],
  USER: [
    { page: "Planning", access: "read" },
    { page: "Relevés d'heures", access: "read" },
  ],
};

// Couleurs des badges par page pour chaque niveau d'accès
const pageColorStyles = {
    "Tous": { full: "bg-red-500 text-white", read: "bg-white text-red-500 border border-red-500" },
    "Planning": { full: "bg-blue-500 text-white", read: "bg-white text-blue-500 border border-blue-500" },
    "Annonces": { full: "bg-green-500 text-white", read: "bg-white text-green-500 border border-green-500" },
    "Candidat": { full: "bg-yellow-400 text-black", read: "bg-white text-yellow-500 border border-yellow-400" },
    "Commande": { full: "bg-cyan-500 text-white", read: "bg-white text-cyan-500 border border-cyan-500" },
    "Relevés d'heures": { full: "bg-purple-500 text-white", read: "bg-white text-purple-500 border border-purple-500" },
    "Facture": { full: "bg-pink-500 text-white", read: "bg-white text-pink-500 border border-pink-500" },
    "Prolongation": { full: "bg-teal-500 text-white", read: "bg-white text-teal-500 border border-teal-500" },
    "Devis": { full: "bg-gray-500 text-white", read: "bg-white text-gray-500 border border-gray-500" },
    "Aide en ligne": { full: "bg-orange-500 text-white", read: "bg-white text-orange-500 border border-orange-500" },
    "Contrat": { full: "bg-lime-500 text-white", read: "bg-white text-lime-500 border border-lime-500" },
    "tableau de bord": { full: "bg-red-500 text-white", read: "bg-white text-red-500 border border-red-500" },
    "Utilisateur": { full: "bg-fuchsia-500 text-white", read: "bg-white text-fuchsia-500 border border-fuchsia-500" },
    "Document": { full: "bg-amber-500 text-white", read: "bg-white text-amber-500 border border-amber-500" },
};
const defaultColors = { full: "bg-gray-200 text-gray-800", read: "bg-white text-gray-500 border border-gray-400"};

const permissionTextColorStyles = {
    "Planning": { read: "text-gray-600", full: "text-gray-700" },
    "Aide en ligne": { read: "text-gray-600", full: "text-gray-700" },
    "Candidat": { read: "text-gray-600", full: "text-gray-700" },
    "Commande": { read: "text-gary-600", full: "text-gray-700" },
    "Relevés d'heures": { read: "text-gray-600", full: "text-gray-700" },
    "Facture": { read: "text-gray-600", full: "text-gray-700" },
    "Prolongation": { read: "text-gray-600", full: "text-gray-700" },
    "Devis": { read: "text-gray-600", full: "text-gray-700" },
    "Document": { read: "text-gray-600", full: "text-gray-700" },
    "Annonces": { read: "text-gray-600", full: "text-gray-700" },
    "Contrat": { read: "text-gray-600", full: "text-gray-700" },
    "Tableau de bord": { read: "text-gray-600", full: "text-gray-700" },
    "Utilisateur": { read: "text-gray-600", full: "text-gray-700" },
};
const defaultTextColors = { read: "text-gray-600", full: "text-gray-700" };

const allAvailablePages = [
  "Tableau de bord", "Annonces", "Commande", "Devis", "Planning", "Candidat", "Contrat", "Prolongation","Relevés d'heures", 
  "Facture", "Document", "Utilisateur", "Aide en ligne"];

const getPermissionsForRole = (role) => {
  return allAvailablePages.map((page) => {
    switch (role) {
      case "USER":
        return { page, access: "none" };

      case "ADMIN":
        return {
          page,
          access: ["Devis", "Utilisateur", "Contrat", "Facture"].includes(page)
            ? "none"
            : "full",
        };

      case "SUPERADMIN":
        return {
          page,
          access: ["Devis", "Utilisateur"].includes(page)
            ? "none"
            : "full",
        };

      case "HYPERADMIN":
        return { page, access: "full" };

      default:
        return { page, access: "none" };
    }
  });
};

const initialCreateState = {
  name: "",
  email: "",
  phone: "",
  role: "USER",
  permissions: getPermissionsForRole("USER"),
};

export default function Utilisateurs() {
  const [activeTab, setActiveTab] = useState("actif");
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState(mockUsers);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState(initialCreateState);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [showFilters, setShowFilters] = useState(false);

  const [editFormData, setEditFormData] = useState({
    role: "",
    email: "",
    phone: "",
    permissions: [],
  });

  // Statistiques des utilisateurs
  const [stats, setStats] = useState({
    total: 0,
    actifs: 0,
    inactifs: 0,
    admins: 0
  });

  // Fonction pour calculer les statistiques
  const calculateStats = (usersList) => {
    const total = usersList.length;
    const actifs = usersList.filter(u => u.active).length;
    const inactifs = usersList.filter(u => !u.active).length;
    const admins = usersList.filter(u => ['HYPERADMIN', 'SUPERADMIN', 'ADMIN'].includes(u.role)).length;

    setStats({ total, actifs, inactifs, admins });
  };

  // Calculer les stats au chargement
  React.useEffect(() => {
    calculateStats(users);
  }, [users]);

  // Filtrer les utilisateurs selon le tab actif et le terme de recherche
  const filteredUsers = users.filter(
    (user) =>
      (user.active === (activeTab === "actif")) &&
      (user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Changer le statut d'un utilisateur
  const toggleUserStatus = (userId) => {
    setUsers(
      users.map((user) => {
        if (user.id === userId) {
          return { ...user, active: !user.active };
        }
        return user;
      }),
    );
  };

  // Changer le rôle d'un utilisateur
  const changeUserRole = (userId, newRole) => {
    setUsers(
      users.map((user) => {
        if (user.id === userId) {
          return { ...user, role: newRole };
        }
        return user;
      }),
    );
  };

  // Ouvrir le dialogue d'édition
  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditFormData({
      role: user.role,
      email: user.email,
      phone: user.phone,
      permissions: user.permissions,
    });
    setIsEditDialogOpen(true);
  };

  const handleRoleTemplateChange = (newRole) => {
    setEditFormData(prev => ({
      ...prev,
      role: newRole,
      permissions: getPermissionsForRole(newRole)
    }));
  };

  const handlePermissionChange = (page, access) => {
    setEditFormData(prev => {
      const newPermissions = [...prev.permissions];
      const permIndex = newPermissions.findIndex(p => p.page === page);

      if (access === "none") {
        if (permIndex > -1) {
          newPermissions.splice(permIndex, 1);
        }
      } else {
        if (permIndex > -1) {
          newPermissions[permIndex].access = access;
        } else {
          newPermissions.push({ page, access });
        }
      }
      return { ...prev, permissions: newPermissions, role: "Personnalisé" };
    });
  };

  // Sauvegarder les modifications
  const saveUserChanges = () => {
    if (!editingUser) return;

    setUsers(users.map(user => {
      if (user.id === editingUser.id) {
        return {
          ...user,
          role: editFormData.role,
          email: editFormData.email,
          phone: editFormData.phone,
          permissions: editFormData.permissions
        };
      }
      return user;
    }));

    setIsEditDialogOpen(false);
  };

  const handleDeleteRequest = (user) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      setUsers(users.filter((user) => user.id !== userToDelete.id));
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCreateUser = () => {
    // Validation simple
    if (!createFormData.name || !createFormData.email) {
      alert("Le nom et l'email sont obligatoires.");
      return;
    }
    const newUser = {
      id: Math.max(...users.map(u => u.id), 0) + 1,
      username: createFormData.email.split('@')[0], // Générer un username
      ...createFormData,
      active: true, // Actif par défaut
    };
    setUsers([...users, newUser]);
    setIsCreateDialogOpen(false);
  };

  const handleCreateRoleTemplateChange = (selectedRole) => {
  setCreateFormData((prev) => ({
    ...prev,
    role: selectedRole,
    permissions: getPermissionsForRole(selectedRole),
  }));
};

  const handleCreatePermissionChange = (page, access) => {
    setCreateFormData(prev => {
      const newPermissions = [...prev.permissions];
      const permIndex = newPermissions.findIndex(p => p.page === page);

      if (access === "none") {
        if (permIndex > -1) {
          newPermissions.splice(permIndex, 1);
        }
      } else {
        if (permIndex > -1) {
          newPermissions[permIndex].access = access;
        } else {
          newPermissions.push({ page, access });
        }
      }
      return { ...prev, permissions: newPermissions, role: "Personnalisé" };
    });
  };

  return (
    <div className="w-full min-h-screen ">
      <div className="w-full p-8">
        {/* Header Section */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  Gestion des Utilisateurs
                </h1>
                <p className="text-lg text-slate-600">
                  Ajoutez, modifiez et organisez les membres de votre équipe
                </p>
              </div>
            </div>

            <motion.button
              onClick={() => setIsCreateDialogOpen(true)}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <UserPlusIcon className="w-5 h-5" />
              <span>Nouvel utilisateur</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Statistiques */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total utilisateurs</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Actifs</p>
                <p className="text-3xl font-bold text-green-600">{stats.actifs}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Inactifs</p>
                <p className="text-3xl font-bold text-red-600">{stats.inactifs}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Administrateurs</p>
                <p className="text-3xl font-bold text-purple-600">{stats.admins}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Contrôles et filtres */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative flex-1 min-w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher un utilisateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveTab("actif")}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === "actif"
                      ? "bg-blue-100 text-blue-700 border border-blue-200"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <UserCheck className="w-4 h-4 inline mr-2" />
                  Actifs
                </button>
                <button
                  onClick={() => setActiveTab("inactif")}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === "inactif"
                      ? "bg-red-100 text-red-700 border border-red-200"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <UserX className="w-4 h-4 inline mr-2" />
                  Inactifs
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                title={viewMode === 'grid' ? 'Vue liste' : 'Vue grille'}
              >
                {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                title="Filtres"
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Table des utilisateurs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <UsersTable
            users={filteredUsers}
            toggleStatus={toggleUserStatus}
            onEdit={openEditDialog}
            onDelete={handleDeleteRequest}
            viewMode={viewMode}
          />
        </motion.div>

        {/* Dialog d'édition utilisateur */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-3xl bg-gray-50">
            <DialogHeader className="p-6">
              <DialogTitle className="text-2xl font-bold text-gray-800">Modifier l'utilisateur</DialogTitle>
              <DialogDescription className="text-gray-600">
                Ajustez les informations et définissez des permissions granulaires pour {editingUser?.name}.
              </DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-6 space-y-6 max-h-[65vh] overflow-y-auto">
              {/* Section Détails */}
              <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Détails de l'utilisateur</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Section Permissions */}
              <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="role-template" className="font-medium text-gray-700">Appliquer un modèle de rôle</Label>
                    <p className="text-xs text-gray-500 mb-2">Change toutes les permissions ci-dessous.</p>
                     <Select
                      value={editFormData.role}
                      onValueChange={handleRoleTemplateChange}
                    >
                      <SelectTrigger id="role-template" className="w-full">
                        <SelectValue placeholder="Choisir un modèle..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white" >
                        {roles.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                   <div className="md:col-span-2">
                      <Label className="font-medium text-gray-700">Permissions personnalisées</Label>
                      <p className="text-xs text-gray-500 mb-2">Ajustez l'accès pour chaque page individuellement.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 p-4 border rounded-md bg-gray-50/50">
                        {allAvailablePages.map((page) => {
    const currentPermission = editFormData.permissions.find(p => p.page === page);
    const currentAccess = currentPermission ? currentPermission.access : "none";
    const colors = permissionTextColorStyles[page] || defaultTextColors;
    return (
      <div key={page} className="flex flex-col space-y-1">
        <Label htmlFor={`edit-perm-${page}`} className="text-sm font-normal text-gray-600">{page}</Label>
        <Select
          value={currentAccess}
          onValueChange={(value) => handlePermissionChange(page, value)}
        >
          <SelectTrigger id={`edit-perm-${page}`} className="h-9 text-xs bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="none" className="text-gray-500">Aucun accès</SelectItem>
            <SelectItem value="read" className={`${colors.read} font-medium`}>Lecture seule</SelectItem>
            <SelectItem value="full" className={`${colors.full} font-bold`}>Accès complet</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  })}

                      </div>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="p-6 bg-gray-100 border-t rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="button" onClick={saveUserChanges} className="bg-blue-600 hover:bg-blue-700 text-white">
                Enregistrer les modifications
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de création utilisateur */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-3xl bg-gray-50">
            <DialogHeader className="p-6">
              <DialogTitle className="text-2xl font-bold text-gray-800">Créer un nouvel utilisateur</DialogTitle>
              <DialogDescription className="text-gray-600">
                Remplissez les informations ci-dessous et définissez les permissions.
              </DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-6 space-y-6 max-h-[65vh] overflow-y-auto">
               <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Détails du nouvel utilisateur</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="space-y-1">
                    <Label htmlFor="create-name">Nom complet</Label>
                    <Input id="create-name" value={createFormData.name} onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})} />
                  </div>
                   <div className="space-y-1">
                    <Label htmlFor="create-email">Email</Label>
                    <Input id="create-email" type="email" value={createFormData.email} onChange={(e) => setCreateFormData({...createFormData, email: e.target.value})} />
                  </div>
                   <div className="space-y-1">
                    <Label htmlFor="create-phone">Téléphone</Label>
                    <Input id="create-phone" value={createFormData.phone} onChange={(e) => setCreateFormData({...createFormData, phone: e.target.value})} />
                  </div>
                </div>
              </div>

               <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Permissions</h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                     <Label htmlFor="create-role-template" className="font-medium text-gray-700">Appliquer un modèle</Label>
                     <p className="text-xs text-gray-500 mb-2">Change toutes les permissions ci-dessous.</p>
                     <Select value={createFormData.role} onValueChange={handleCreateRoleTemplateChange}>
                      <SelectTrigger id="create-role-template"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {roles.map((role) => <SelectItem key={role} value={role}>{role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                     <Label className="font-medium text-gray-700">Permissions personnalisées</Label>
                     <p className="text-xs text-gray-500 mb-2">Ajustez l'accès pour chaque page.</p>
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 p-4 border rounded-md bg-gray-50/50">
                      {allAvailablePages.map((page) => {
                        const currentPermission = createFormData.permissions.find(p => p.page === page);
                        const currentAccess = currentPermission ? currentPermission.access : "none";
                        const colors = permissionTextColorStyles[page] || defaultTextColors;
                        return (
                          <div key={page} className="flex flex-col space-y-1">
                            <Label htmlFor={`create-perm-${page}`} className="text-sm font-normal text-gray-600">{page}</Label>
                            <Select value={currentAccess} onValueChange={(value) => handleCreatePermissionChange(page, value)}>
                              <SelectTrigger id={`create-perm-${page}`} className="h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-white">
                                <SelectItem value="none" className="text-gray-500">Aucun accès</SelectItem>
                                <SelectItem value="read" className={`${colors.read} font-medium`}>Lecture seule</SelectItem>
                                <SelectItem value="full" className={`${colors.full} font-bold`}>Accès complet</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
             <DialogFooter className="p-6 bg-gray-100 border-t rounded-b-lg">
              <Button type="button" variant="outline" className="bg-white text-black" onClick={() => setIsCreateDialogOpen(false)}>Annuler</Button>
              <Button type="button" onClick={handleCreateUser} className="bg-black hover:bg-black text-white">Créer l'utilisateur</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmation de suppression */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Êtes-vous sûr de vouloir supprimer cet utilisateur ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. L'utilisateur{" "}
                <span className="font-bold text-gray-800">{userToDelete?.name}</span>{" "}
                sera définitivement supprimé.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Confirmer la suppression
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// Composant Tableau d'utilisateurs moderne
function UsersTable({
  users,
  toggleStatus,
  onEdit,
  onDelete,
  viewMode = 'grid'
}) {
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.length > 0 ? (
          users.map((user) => (
            <motion.div
              key={user.id}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
              whileHover={{ y: -2 }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-12 w-12 border-2 border-slate-100">
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback className="bg-blue-100 text-blue-600 font-semibold">
                      {user.name
                        .split(" ")
                        .map((name) => name[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-slate-900">{user.name}</h3>
                    <p className="text-sm text-slate-500">@{user.username}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEdit(user)}
                    className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                    title="Modifier"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-sm text-slate-600">
                  <span className="font-medium">Email:</span>
                  <span>{user.email}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-slate-600">
                  <span className="font-medium">Téléphone:</span>
                  <span>{user.phone}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs font-medium ${user.active ? 'text-green-700' : 'text-red-700'}`}>
                      {user.active ? 'ACTIF' : 'INACTIF'}
                    </span>
                    <button
                      onClick={() => toggleStatus(user.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        user.active ? 'bg-green-500' : 'bg-red-400'
                      }`}
                      role="switch"
                      aria-checked={user.active}
                      title={user.active ? 'Désactiver l\'utilisateur' : 'Activer l\'utilisateur'}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          user.active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    user.role === 'HYPERADMIN' ? 'bg-red-100 text-red-700' :
                    user.role === 'SUPERADMIN' ? 'bg-orange-100 text-orange-700' :
                    user.role === 'ADMIN' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {user.role}
                  </span>
                </div>

                <div className="pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Permissions:</p>
                  <div className="flex flex-wrap gap-1">
                    {(user.permissions || []).slice(0, 3).map((permission) => {
                      const styles = pageColorStyles[permission.page] || defaultColors;
                      let badgeClass = "";
                      if (permission.access === "full") {
                        badgeClass = styles.full;
                      } else if (permission.access === "read") {
                        badgeClass = styles.read;
                      } else {
                        return null;
                      }

                      return (
                        <span
                          key={permission.page}
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${badgeClass}`}
                        >
                          {permission.page}
                        </span>
                      );
                    })}
                    {user.permissions && user.permissions.length > 3 && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                        +{user.permissions.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">Aucun utilisateur trouvé</p>
          </div>
        )}
      </div>
    );
  }

  // Vue liste (tableau)
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Utilisateur
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Rôle
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.length > 0 ? (
              users.map((user) => (
                <motion.tr
                  key={user.id}
                  className="hover:bg-slate-50 transition-colors"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10 border">
                        <AvatarImage src={user.avatar} />
                        <AvatarFallback className="bg-blue-100 text-blue-600 text-sm font-medium">
                          {user.name
                            .split(" ")
                            .map((name) => name[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-slate-900">{user.name}</div>
                        <div className="text-sm text-slate-500">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-900">{user.email}</div>
                    <div className="text-sm text-slate-500">{user.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                      user.role === 'HYPERADMIN' ? 'bg-red-100 text-red-700' :
                      user.role === 'SUPERADMIN' ? 'bg-orange-100 text-orange-700' :
                      user.role === 'ADMIN' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs font-medium ${user.active ? 'text-green-700' : 'text-red-700'}`}>
                        {user.active ? 'ACTIF' : 'INACTIF'}
                      </span>
                      <button
                        onClick={() => toggleStatus(user.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          user.active ? 'bg-green-500' : 'bg-red-400'
                        }`}
                        role="switch"
                        aria-checked={user.active}
                        title={user.active ? 'Désactiver l\'utilisateur' : 'Activer l\'utilisateur'}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            user.active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(user.permissions || []).slice(0, 2).map((permission) => {
                        const styles = pageColorStyles[permission.page] || defaultColors;
                        let badgeClass = "";
                        if (permission.access === "full") {
                          badgeClass = styles.full;
                        } else if (permission.access === "read") {
                          badgeClass = styles.read;
                        } else {
                          return null;
                        }

                        return (
                          <span
                            key={permission.page}
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${badgeClass}`}
                          >
                            {permission.page}
                          </span>
                        );
                      })}
                      {user.permissions && user.permissions.length > 2 && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                          +{user.permissions.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onEdit(user)}
                        className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(user)}
                        className="p-2 rounded-lg text-slate-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 text-lg">Aucun utilisateur trouvé</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
